import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useFieldArray, useForm, UseFormRegister, Control, FieldErrors } from "react-hook-form";
import { Blockquote, Button, Card, Divider, ErrorMessage, Icon, Loading, TextArea, Tooltip, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useCloseJobWithExtrasMutation } from "@/redux/apis/booking/endpoints/closeJob";
import { BookingDetails } from "@/redux/slices/booking/types";
import { addToast, getAssetUrl } from "@/utils";
import { useBookingFees } from "../../../hooks/useBookingFees";
import { useSelectedBookingId } from "../../../hooks/useSelectedBookingId";
import { WaitTimeCard, AirportParkingChargesCard, AdditionalUnscheduledStopsCard, TollChargesCard, OtherChargesCard } from "./components";
import {
  FormData,
  schema,
  WaitTimeFormData,
  AirportParkingFormData,
  AdditionalStopsFormData,
  TollChargesFormData,
  OtherChargesFormData,
} from "./types";

const defaultValues: Partial<FormData> = {
  airportParkingChargesType: "out-of-pocket",
  tollChargesType: "out-of-pocket",
  providerNotes: "",
  clientNotes: "",
  otherCharges: [],
};

interface CloseNoExtrasProps {
  booking: BookingDetails;
  onClose: () => void;
  noShow?: boolean;
}

export const CloseWithExtras = ({ booking, onClose, noShow = false }: CloseNoExtrasProps) => {
  const { id: bookingId, jobNumber, driver, transferType, closeMeta, status } = booking;
  const [_, setSelectedBookingId] = useSelectedBookingId();
  const [closeJobWithExtras, { isLoading: isClosingJob }] = useCloseJobWithExtrasMutation();

  const formValues = useMemo(() => {
    if (!closeMeta || !["closed", "complete_awaiting_review"].includes(status)) return undefined;

    const { waitTime, unscheduledStops, parkingCharges, tollCharges, otherExtras, additionalRunTime, clientNotes, providerNotes } =
      closeMeta;

    return {
      additionalWaitingTime: Number(waitTime?.name) || undefined,
      additionalWaitingTimeFee: Number(waitTime?.cost) || undefined,
      additionalUnscheduledStops: Number(unscheduledStops?.name) || undefined,
      additionalUnscheduledStopsFee: Number(unscheduledStops?.cost) || undefined,
      additionalRunTime: Number(additionalRunTime?.name) || undefined,
      additionalRunTimeFee: Number(additionalRunTime?.cost) || undefined,
      airportParkingCharges: Number(parkingCharges?.cost) || undefined,
      airportParkingChargesType: parkingCharges?.type || "general",
      tollCharges: Number(tollCharges?.cost) || undefined,
      tollChargesType: tollCharges?.type || "general",
      otherCharges: otherExtras?.map((extra) => ({
        type: extra.type ?? "general",
        description: extra.name,
        amount: extra.cost,
      })),
      providerNotes: providerNotes,
      clientNotes: clientNotes,
    } as Partial<FormData>;
  }, [closeMeta, status]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: formValues || defaultValues,
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "otherCharges",
  });

  const additionalWaitingTime = watch("additionalWaitingTime");
  const additionalRunTime = watch("additionalRunTime");
  const unscheduledStops = watch("additionalUnscheduledStops");

  const {
    unscheduledStopsFee,
    additionalRunTimeFee,
    additionalWaitTimeFee,
    isLoading: isLoadingFees,
  } = useBookingFees(booking.id, {
    additional_run_time: additionalRunTime || undefined,
    unscheduled_stops: unscheduledStops || undefined,
    wait_time: additionalWaitingTime || undefined,
  });

  const onSubmit = async (data: FormData) => {
    return closeJobWithExtras({
      bookingId,
      noShow,
      unscheduled_stops: data.additionalUnscheduledStops
        ? {
            amount: unscheduledStopsFee ? unscheduledStopsFee * 100 : 0,
            description: data.additionalUnscheduledStops.toString(),
            type: "general",
          }
        : undefined,
      wait_time: data.additionalWaitingTime
        ? {
            amount: additionalWaitTimeFee * 100,
            description: data.additionalWaitingTime.toString(),
            type: "general",
          }
        : undefined,
      additional_run_time: data.additionalRunTime
        ? {
            amount: additionalRunTimeFee ? additionalRunTimeFee * 100 : 0,
            description: data.additionalRunTime.toString(),
            type: "general",
          }
        : undefined,
      parking_charges: data.airportParkingCharges
        ? {
            amount: data.airportParkingCharges * 100,
            description: data.airportParkingCharges.toString(),
            type: data.airportParkingChargesType,
          }
        : undefined,
      toll_charges: data.tollCharges
        ? {
            amount: data.tollCharges * 100,
            description: data.tollCharges.toString(),
            type: data.tollChargesType,
          }
        : undefined,
      provider_notes: data.providerNotes,
      client_notes: data.clientNotes,
      other_extra: data.otherCharges.map((i) => ({
        amount: i.amount * 100,
        description: i.description,
        type: i.type,
      })),
    })
      .unwrap()
      .then(() => {
        if (noShow) addToast("success", `Successfully Closed Job ${jobNumber} with “No Show - With Extras“`);
        else addToast("success", `Successfully Closed Job ${jobNumber} with “Completed - With Extras“`);
        setSelectedBookingId(undefined);
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const isHourlyOrDailyRate = transferType.type === "hourly_rate" || transferType.type === "daily_rate";
  const waitTimeForm = {
    register: register as unknown as UseFormRegister<WaitTimeFormData>,
    errors: errors as unknown as FieldErrors<WaitTimeFormData>,
  };

  const airportParkingForm = {
    register: register as unknown as UseFormRegister<AirportParkingFormData>,
    errors: errors as unknown as FieldErrors<AirportParkingFormData>,
    control: control as unknown as Control<AirportParkingFormData>,
  };

  const additionalStopsForm = {
    register: register as unknown as UseFormRegister<AdditionalStopsFormData>,
    errors: errors as unknown as FieldErrors<AdditionalStopsFormData>,
  };

  const tollChargesForm = {
    register: register as unknown as UseFormRegister<TollChargesFormData>,
    errors: errors as unknown as FieldErrors<TollChargesFormData>,
    control: control as unknown as Control<TollChargesFormData>,
  };

  const otherChargesForm = {
    register: register as unknown as UseFormRegister<OtherChargesFormData>,
    control: control as unknown as Control<OtherChargesFormData>,
    errors: errors as unknown as FieldErrors<OtherChargesFormData>,
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-5 flex flex-1 flex-col">
      <div className="flex-1 space-y-5 pb-5">
        <WaitTimeCard
          {...waitTimeForm}
          isHourlyOrDailyRate={isHourlyOrDailyRate}
          additionalWaitingTime={additionalWaitingTime}
          additionalRunTime={additionalRunTime}
          additionalWaitTimeFee={additionalWaitTimeFee}
          additionalRunTimeFee={additionalRunTimeFee}
          isLoadingFees={isLoadingFees}
        />

        <AirportParkingChargesCard {...airportParkingForm} />

        <AdditionalUnscheduledStopsCard
          {...additionalStopsForm}
          unscheduledStops={unscheduledStops}
          unscheduledStopsFee={unscheduledStopsFee}
          isLoadingFees={isLoadingFees}
        />

        <TollChargesCard {...tollChargesForm} />

        <OtherChargesCard {...otherChargesForm} fields={fields} append={append} remove={remove} />

        <div className="space-y-3">
          <Typography variant="title">Notes</Typography>
          <Card as="fieldset">
            <Typography variant="action" className="mb-1 flex place-items-center gap-2 text-neutral-dark-gray">
              Notes for Editing Extras
            </Typography>
            <TextArea {...register("providerNotes")} placeholder="Enter Notes" rows={3} required={isDirty} />
            <Typography className="mt-1 leading-loose text-neutral-silver-gray">This will also be shared with the driver</Typography>
            <ErrorMessage errors={errors} name="providerNotes" />
          </Card>

          {noShow && (
            <Card as="fieldset">
              <Typography variant="action" className="mb-1 flex items-center gap-1 text-neutral-dark-gray">
                Notes to Customer
                <Tooltip
                  content="We recommend leaving notes explaining why this job is being closed with a charge. These notes will be displayed to the customer."
                  placement="right"
                  maxWidth={320}
                >
                  <i className="inline-flex">
                    <Icon name="InfoCircle" variant="Bold" size="lg" className="inline rotate-180 text-neutral-dark-gray" />
                  </i>
                </Tooltip>
              </Typography>
              <TextArea {...register("clientNotes")} placeholder="Enter Notes" rows={3} />
              <ErrorMessage errors={errors} name="clientNotes" className="mt-2" />
            </Card>
          )}
        </div>

        {closeMeta?.driverNotes && (
          <Blockquote avatarURL={getAssetUrl(driver?.avatar)} heading={driver!.name}>
            {closeMeta?.driverNotes}
          </Blockquote>
        )}
      </div>
      <div className="w-full">
        <Divider className="-mx-5" />
        <Button type="submit" disabled={isClosingJob} className="relative mt-5 w-full" variant="primary" size="lg">
          {isClosingJob && <Loading className="rounded-md" />}
          Approve For Payment
        </Button>
      </div>
    </form>
  );
};
